import { useEffect, useState } from 'react';
import axios from 'axios';
import { Card } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '@/components/ui/alert-dialog';
import { Plus, Edit, Trash2, Search, Beef, ArrowLeft, ChevronRight, Tag } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const TipoGanados = () => {
  // TipoGanados state
  const [tipoGanados, setTipoGanados] = useState([]);
  const [filteredTipoGanados, setFilteredTipoGanados] = useState([]);
  const [loadingTipoGanados, setLoadingTipoGanados] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');

  // TipoGanado dialog
  const [showTipoGanadoDialog, setShowTipoGanadoDialog] = useState(false);
  const [editingTipoGanadoId, setEditingTipoGanadoId] = useState(null);
  const [tipoGanadoFormNombre, setTipoGanadoFormNombre] = useState('');
  const [savingTipoGanado, setSavingTipoGanado] = useState(false);
  const [deleteTipoGanadoId, setDeleteTipoGanadoId] = useState(null);

  // Drill-down: selected tipoGanado
  const [selectedTipoGanado, setSelectedTipoGanado] = useState(null);

  // Categorias state (for drill-down view)
  const [categorias, setCategorias] = useState([]);
  const [filteredCategorias, setFilteredCategorias] = useState([]);
  const [loadingCategorias, setLoadingCategorias] = useState(false);
  const [categoriaSearchTerm, setCategoriaSearchTerm] = useState('');

  // Categoria dialog
  const [showCategoriaDialog, setShowCategoriaDialog] = useState(false);
  const [editingCategoriaId, setEditingCategoriaId] = useState(null);
  const [categoriaFormNombre, setCategoriaFormNombre] = useState('');
  const [savingCategoria, setSavingCategoria] = useState(false);
  const [deleteCategoriaId, setDeleteCategoriaId] = useState(null);


  // Razas state (for drill-down view)
  const [razas, setRazas] = useState([]);
  const [filteredRazas, setFilteredRazas] = useState([]);
  const [loadingRazas, setLoadingRazas] = useState(false);
  const [razaSearchTerm, setRazaSearchTerm] = useState('');

  // Raza dialog
  const [showRazaDialog, setShowRazaDialog] = useState(false);
  const [editingRazaId, setEditingRazaId] = useState(null);
  const [razaFormNombre, setRazaFormNombre] = useState('');
  const [savingRaza, setSavingRaza] = useState(false);
  const [deleteRazaId, setDeleteRazaId] = useState(null);

  // Model counts per tipoGanado
  const [modelCounts, setModelCounts] = useState({});
  const [razaCounts, setRazaCounts] = useState({});

  // Active Tab
  const [activeTab, setActiveTab] = useState('categorias');


  useEffect(() => {
    loadTipoGanados();
  }, []);

  useEffect(() => {
    applyTipoGanadoFilters();
  }, [tipoGanados, searchTerm]);

  useEffect(() => {
    applyCategoriaFilters();
  }, [categorias, categoriaSearchTerm]);

  useEffect(() => {
    applyRazaFilters();
  }, [razas, razaSearchTerm]);

  const loadTipoGanados = async () => {
    try {
      const [tipoGanadosRes, categoriasRes, razasRes] = await Promise.all([
        axios.get(`${API}/tipoGanados`),
        axios.get(`${API}/categorias`),
        axios.get(`${API}/razas`)
      ]);
      setTipoGanados(tipoGanadosRes.data);

      // Count models per tipoGanado
      const catCounts = {};
      categoriasRes.data.forEach(m => {
        catCounts[m.tipoGanadoId] = (catCounts[m.tipoGanadoId] || 0) + 1;
      });
      setModelCounts(catCounts);

      const rzCounts = {};
      razasRes.data.forEach(m => {
        rzCounts[m.tipoGanadoId] = (rzCounts[m.tipoGanadoId] || 0) + 1;
      });
      setRazaCounts(rzCounts);
    } catch (error) {
      console.error('Error loading tipoGanados:', error);
      toast.error('Error al cargar tipoGanados');
    } finally {
      setLoadingTipoGanados(false);
    }
  };

  const loadCategorias = async (tipoGanadoId) => {
    setLoadingCategorias(true);
    try {
      const response = await axios.get(`${API}/tipoGanados/${tipoGanadoId}/categorias`);
      setCategorias(response.data);
    } catch (error) {
      console.error('Error loading categorias:', error);
      toast.error('Error al cargar categorias');
    } finally {
      setLoadingCategorias(false);
    }
  };

  const applyTipoGanadoFilters = () => {
    let filtered = [...tipoGanados];
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(m => m.nombre.toLowerCase().includes(term));
    }
    setFilteredTipoGanados(filtered);
  };

  const applyCategoriaFilters = () => {
    let filtered = [...categorias];
    if (categoriaSearchTerm) {
      const term = categoriaSearchTerm.toLowerCase();
      filtered = filtered.filter(m => m.nombre.toLowerCase().includes(term));
    }
    setFilteredCategorias(filtered);
  };


  const loadRazas = async (tipoGanadoId) => {
    setLoadingRazas(true);
    try {
      const response = await axios.get(`${API}/tipoGanados/${tipoGanadoId}/razas`);
      setRazas(response.data);
    } catch (error) {
      console.error('Error loading razas:', error);
      toast.error('Error al cargar razas');
    } finally {
      setLoadingRazas(false);
    }
  };

  const applyRazaFilters = () => {
    let filtered = [...razas];
    if (razaSearchTerm) {
      const term = razaSearchTerm.toLowerCase();
      filtered = filtered.filter(m => m.nombre.toLowerCase().includes(term));
    }
    setFilteredRazas(filtered);
  };

  // ========== TipoGanado handlers ==========

  const handleOpenTipoGanadoDialog = (tipoGanado = null) => {
    if (tipoGanado) {
      setEditingTipoGanadoId(tipoGanado.id);
      setTipoGanadoFormNombre(tipoGanado.nombre);
    } else {
      setEditingTipoGanadoId(null);
      setTipoGanadoFormNombre('');
    }
    setShowTipoGanadoDialog(true);
  };

  const handleSaveTipoGanado = async () => {
    if (!tipoGanadoFormNombre.trim()) {
      toast.error('El nombre es requerido');
      return;
    }
    setSavingTipoGanado(true);
    try {
      if (editingTipoGanadoId) {
        await axios.put(`${API}/tipoGanados/${editingTipoGanadoId}`, { nombre: tipoGanadoFormNombre.trim() });
        toast.success('TipoGanado actualizada');
        // Update selectedTipoGanado if editing the currently selected one
        if (selectedTipoGanado?.id === editingTipoGanadoId) {
          setSelectedTipoGanado(prev => ({ ...prev, nombre: tipoGanadoFormNombre.trim() }));
        }
      } else {
        await axios.post(`${API}/tipoGanados`, { nombre: tipoGanadoFormNombre.trim() });
        toast.success('TipoGanado creada');
      }
      setShowTipoGanadoDialog(false);
      loadTipoGanados();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al guardar tipoGanado');
    } finally {
      setSavingTipoGanado(false);
    }
  };

  const handleDeleteTipoGanado = async (id) => {
    try {
      await axios.delete(`${API}/tipoGanados/${id}`);
      toast.success('TipoGanado eliminada');
      loadTipoGanados();
      if (selectedTipoGanado?.id === id) {
        setSelectedTipoGanado(null);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al eliminar tipoGanado');
    }
    setDeleteTipoGanadoId(null);
  };

  const handleSelectTipoGanado = (tipoGanado) => {
    setSelectedTipoGanado(tipoGanado);
    setCategoriaSearchTerm('');
    setRazaSearchTerm('');
    setActiveTab('categorias');
    loadCategorias(tipoGanado.id);
    loadRazas(tipoGanado.id);
  };

  const handleBackToTipoGanados = () => {
    setSelectedTipoGanado(null);
    setCategorias([]);
    setCategoriaSearchTerm('');
  };

  // ========== Categoria handlers ==========

  const handleOpenCategoriaDialog = (categoria = null) => {
    if (categoria) {
      setEditingCategoriaId(categoria.id);
      setCategoriaFormNombre(categoria.nombre);
    } else {
      setEditingCategoriaId(null);
      setCategoriaFormNombre('');
    }
    setShowCategoriaDialog(true);
  };

  const handleSaveCategoria = async () => {
    if (!categoriaFormNombre.trim()) {
      toast.error('El nombre es requerido');
      return;
    }
    setSavingCategoria(true);
    try {
      if (editingCategoriaId) {
        await axios.put(`${API}/categorias/${editingCategoriaId}`, { nombre: categoriaFormNombre.trim() });
        toast.success('Categoria actualizado');
      } else {
        await axios.post(`${API}/categorias`, {
          tipoGanadoId: selectedTipoGanado.id,
          nombre: categoriaFormNombre.trim()
        });
        toast.success('Categoria creado');
      }
      setShowCategoriaDialog(false);
      loadCategorias(selectedTipoGanado.id);
      loadTipoGanados(); // Refresh counts
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al guardar categoria');
    } finally {
      setSavingCategoria(false);
    }
  };

  const handleDeleteCategoria = async (id) => {
    try {
      await axios.delete(`${API}/categorias/${id}`);
      toast.success('Categoria eliminado');
      loadCategorias(selectedTipoGanado.id);
      loadTipoGanados(); // Refresh counts
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al eliminar categoria');
    }
    setDeleteCategoriaId(null);
  };


  // ========== Raza handlers ==========

  const handleOpenRazaDialog = (raza = null) => {
    if (raza) {
      setEditingRazaId(raza.id);
      setRazaFormNombre(raza.nombre);
    } else {
      setEditingRazaId(null);
      setRazaFormNombre('');
    }
    setShowRazaDialog(true);
  };

  const handleSaveRaza = async () => {
    if (!razaFormNombre.trim()) {
      toast.error('El nombre es requerido');
      return;
    }
    setSavingRaza(true);
    try {
      if (editingRazaId) {
        await axios.put(`${API}/razas/${editingRazaId}`, { nombre: razaFormNombre.trim() });
        toast.success('Raza actualizada');
      } else {
        await axios.post(`${API}/razas`, {
          tipoGanadoId: selectedTipoGanado.id,
          nombre: razaFormNombre.trim()
        });
        toast.success('Raza creada');
      }
      setShowRazaDialog(false);
      loadRazas(selectedTipoGanado.id);
      loadTipoGanados(); // Refresh counts
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al guardar raza');
    } finally {
      setSavingRaza(false);
    }
  };

  const handleDeleteRaza = async (id) => {
    try {
      await axios.delete(`${API}/razas/${id}`);
      toast.success('Raza eliminada');
      loadRazas(selectedTipoGanado.id);
      loadTipoGanados(); // Refresh counts
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al eliminar raza');
    }
    setDeleteRazaId(null);
  };

  if (loadingTipoGanados) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-muted-foreground">Cargando...</div>
      </div>
    );
  }

  // ========== DRILL-DOWN: Categorias view ==========
  if (selectedTipoGanado) {
    return (
      <div className="space-y-6 bg-background min-h-screen">
        <div className="bg-background border-b">
          <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-6">
            <Button variant="ghost" className="mb-4" onClick={handleBackToTipoGanados}>
              <ArrowLeft className="h-4 w-4 mr-2" />
              Volver a TipoGanados
            </Button>
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
              <div>
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-primary/10 rounded-lg">
                    <Beef className="h-6 w-6 text-primary" />
                  </div>
                  <div>
                    <h1 className="text-3xl font-semibold tracking-tight">{selectedTipoGanado.nombre}</h1>
                    <p className="text-muted-foreground mt-1">{categorias.length} categoría(s), {razas.length} raza(s)</p>
                  </div>
                </div>
              </div>
              <Button 
                onClick={() => activeTab === 'categorias' ? handleOpenCategoriaDialog() : handleOpenRazaDialog()} 
              >
                <Plus className="h-4 w-4 mr-2" />
                {activeTab === 'categorias' ? 'Nueva Categoría' : 'Nueva Raza'}
              </Button>
            </div>
          </div>
        </div>

        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
            <TabsList className="grid w-full grid-cols-2 max-w-[400px]">
              <TabsTrigger value="categorias">Categorías</TabsTrigger>
              <TabsTrigger value="razas">Razas</TabsTrigger>
            </TabsList>
            
            <TabsContent value="categorias" className="space-y-6">
              {/* Search Categorias */}
              <Card className="p-4">
                <div className="flex flex-col sm:flex-row gap-4">
                  <div className="flex-1 relative">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                    <Input
                      placeholder="Buscar categoría..."
                      value={categoriaSearchTerm}
                      onChange={(e) => setCategoriaSearchTerm(e.target.value)}
                      className="pl-10"
                      data-testid="search-categoria-input"
                    />
                  </div>
                </div>
              </Card>

              {loadingCategorias ? (
            <div className="flex items-center justify-center min-h-[200px]">
              <div className="text-muted-foreground">Cargando categorias...</div>
            </div>
          ) : filteredCategorias.length === 0 ? (
            <Card className="p-12 text-center">
              <Tag className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
              <p className="text-muted-foreground">
                {categorias.length === 0 ? 'No hay categorias para esta tipoGanado' : 'No se encontraron resultados'}
              </p>
              {categorias.length === 0 && (
                <Button className="mt-4" onClick={() => handleOpenCategoriaDialog()}>
                  <Plus className="h-4 w-4 mr-2" />
                  Agregar primer categoria
                </Button>
              )}
            </Card>
          ) : (
            <div className="grid gap-3 md:grid-cols-2 lg:grid-cols-3">
              {filteredCategorias.map((categoria) => (
                <Card key={categoria.id} className="p-4" data-testid="categoria-card">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="p-1.5 bg-accent rounded-md">
                        <Tag className="h-4 w-4 text-muted-foreground" />
                      </div>
                      <span className="font-medium">{categoria.nombre}</span>
                    </div>
                    <div className="flex gap-1">
                      <Button variant="ghost" size="sm" onClick={() => handleOpenCategoriaDialog(categoria)}>
                        <Edit className="h-4 w-4" />
                      </Button>
                      <Button variant="ghost" size="sm" onClick={() => setDeleteCategoriaId(categoria.id)}>
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                </Card>
              ))}
            </div>
          )}
            </TabsContent>

            <TabsContent value="razas" className="space-y-6">
              {/* Search Razas */}
              <Card className="p-4">
                <div className="flex flex-col sm:flex-row gap-4">
                  <div className="flex-1 relative">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                    <Input
                      placeholder="Buscar raza..."
                      value={razaSearchTerm}
                      onChange={(e) => setRazaSearchTerm(e.target.value)}
                      className="pl-10"
                    />
                  </div>
                </div>
              </Card>

              {loadingRazas ? (
                <div className="flex items-center justify-center min-h-[200px]">
                  <div className="text-muted-foreground">Cargando razas...</div>
                </div>
              ) : filteredRazas.length === 0 ? (
                <Card className="p-12 text-center">
                  <Tag className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                  <p className="text-muted-foreground">
                    {razas.length === 0 ? 'No hay razas para esta tipoGanado' : 'No se encontraron resultados'}
                  </p>
                  {razas.length === 0 && (
                    <Button className="mt-4" onClick={() => handleOpenRazaDialog()}>
                      <Plus className="h-4 w-4 mr-2" />
                      Agregar primera raza
                    </Button>
                  )}
                </Card>
              ) : (
                <div className="grid gap-3 md:grid-cols-2 lg:grid-cols-3">
                  {filteredRazas.map((raza) => (
                    <Card key={raza.id} className="p-4">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <div className="p-1.5 bg-accent rounded-md">
                            <Tag className="h-4 w-4 text-muted-foreground" />
                          </div>
                          <span className="font-medium">{raza.nombre}</span>
                        </div>
                        <div className="flex gap-1">
                          <Button variant="ghost" size="sm" onClick={() => handleOpenRazaDialog(raza)}>
                            <Edit className="h-4 w-4" />
                          </Button>
                          <Button variant="ghost" size="sm" onClick={() => setDeleteRazaId(raza.id)}>
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>
                    </Card>
                  ))}
                </div>
              )}
            </TabsContent>
          </Tabs>
        </div>

        {/* Categoria Dialog */}
        <Dialog open={showCategoriaDialog} onOpenChange={setShowCategoriaDialog}>
          <DialogContent className="max-w-sm">
            <DialogHeader>
              <DialogTitle>{editingCategoriaId ? 'Editar' : 'Nuevo'} Categoria</DialogTitle>
              <DialogDescription>
                {editingCategoriaId ? 'Modifica el nombre del categoria' : `Agregar categoria para ${selectedTipoGanado.nombre}`}
              </DialogDescription>
            </DialogHeader>
            <div className="py-4">
              <div className="space-y-2">
                <Label htmlFor="categoriaNombre">Nombre *</Label>
                <Input
                  id="categoriaNombre"
                  value={categoriaFormNombre}
                  onChange={(e) => setCategoriaFormNombre(e.target.value)}
                  placeholder="Ej. Corolla"
                  autoFocus
                  onKeyDown={(e) => e.key === 'Enter' && handleSaveCategoria()}
                  data-testid="categoria-nombre-input"
                />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowCategoriaDialog(false)} disabled={savingCategoria}>
                Cancelar
              </Button>
              <Button onClick={handleSaveCategoria} disabled={savingCategoria} data-testid="save-categoria-button">
                {savingCategoria ? 'Guardando...' : 'Guardar'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Delete Categoria Confirm */}
        <AlertDialog open={!!deleteCategoriaId} onOpenChange={() => setDeleteCategoriaId(null)}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>¿Eliminar categoria?</AlertDialogTitle>
              <AlertDialogDescription>
                Esta acción no se puede deshacer.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancelar</AlertDialogCancel>
              <AlertDialogAction onClick={() => handleDeleteCategoria(deleteCategoriaId)}>
                Eliminar
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>


        {/* Raza Dialog */}
        <Dialog open={showRazaDialog} onOpenChange={setShowRazaDialog}>
          <DialogContent className="max-w-sm">
            <DialogHeader>
              <DialogTitle>{editingRazaId ? 'Editar' : 'Nueva'} Raza</DialogTitle>
              <DialogDescription>
                {editingRazaId ? 'Modifica el nombre de la raza' : `Agregar raza para ${selectedTipoGanado.nombre}`}
              </DialogDescription>
            </DialogHeader>
            <div className="py-4">
              <div className="space-y-2">
                <Label htmlFor="razaNombre">Nombre *</Label>
                <Input
                  id="razaNombre"
                  value={razaFormNombre}
                  onChange={(e) => setRazaFormNombre(e.target.value)}
                  placeholder="Ej. Hereford"
                  autoFocus
                  onKeyDown={(e) => e.key === 'Enter' && handleSaveRaza()}
                />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowRazaDialog(false)} disabled={savingRaza}>
                Cancelar
              </Button>
              <Button onClick={handleSaveRaza} disabled={savingRaza}>
                {savingRaza ? 'Guardando...' : 'Guardar'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Delete Raza Confirm */}
        <AlertDialog open={!!deleteRazaId} onOpenChange={() => setDeleteRazaId(null)}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>¿Eliminar raza?</AlertDialogTitle>
              <AlertDialogDescription>
                Esta acción no se puede deshacer.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancelar</AlertDialogCancel>
              <AlertDialogAction onClick={() => handleDeleteRaza(deleteRazaId)}>
                Eliminar
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>

      </div>
    );
  }

  // ========== MAIN: TipoGanados list view ==========
  return (
    <div className="space-y-6 bg-background min-h-screen">
      <div className="bg-background border-b">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div>
              <h1 className="text-3xl font-semibold tracking-tight">TipoGanados</h1>
              <p className="text-muted-foreground mt-1">{tipoGanados.length} tipoGanados registradas</p>
            </div>
            <Button onClick={() => handleOpenTipoGanadoDialog()} data-testid="new-tipoGanado-button">
              <Plus className="h-4 w-4 mr-2" />
              Nueva TipoGanado
            </Button>
          </div>
        </div>
      </div>

      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        {/* Filtros */}
        <Card className="p-4 mb-6">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Buscar tipoGanado..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
                data-testid="search-tipoGanado-input"
              />
            </div>
          </div>
        </Card>

        {filteredTipoGanados.length === 0 ? (
          <Card className="p-12 text-center">
            <Beef className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
            <p className="text-muted-foreground">
              {tipoGanados.length === 0 ? 'No hay tipoGanados creadas' : 'No se encontraron resultados'}
            </p>
            {tipoGanados.length === 0 && (
              <Button className="mt-4" onClick={() => handleOpenTipoGanadoDialog()}>
                <Plus className="h-4 w-4 mr-2" />
                Crear primera tipoGanado
              </Button>
            )}
          </Card>
        ) : (
          <div className="grid gap-3 md:grid-cols-2 lg:grid-cols-3">
            {filteredTipoGanados.map((tipoGanado) => (
              <Card
                key={tipoGanado.id}
                className="p-5 cursor-pointer hover:border-primary/50 transition-colors group"
                onClick={() => handleSelectTipoGanado(tipoGanado)}
                data-testid="tipoGanado-card"
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-primary/10 rounded-lg group-hover:bg-primary/20 transition-colors">
                      <Beef className="h-5 w-5 text-primary" />
                    </div>
                    <div>
                      <h3 className="font-semibold">{tipoGanado.nombre}</h3>
                      <p className="text-xs text-muted-foreground mt-0.5">
                        {modelCounts[tipoGanado.id] || 0} categoría(s), {razaCounts[tipoGanado.id] || 0} raza(s)
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-1">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={(e) => { e.stopPropagation(); handleOpenTipoGanadoDialog(tipoGanado); }}
                      className="opacity-0 group-hover:opacity-100 transition-opacity"
                    >
                      <Edit className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={(e) => { e.stopPropagation(); setDeleteTipoGanadoId(tipoGanado.id); }}
                      className="opacity-0 group-hover:opacity-100 transition-opacity"
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                    <ChevronRight className="h-5 w-5 text-muted-foreground ml-1" />
                  </div>
                </div>
              </Card>
            ))}
          </div>
        )}
      </div>

      {/* TipoGanado Dialog */}
      <Dialog open={showTipoGanadoDialog} onOpenChange={setShowTipoGanadoDialog}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>{editingTipoGanadoId ? 'Editar' : 'Nueva'} TipoGanado</DialogTitle>
            <DialogDescription>
              {editingTipoGanadoId ? 'Modifica el nombre de la tipoGanado' : 'Ingrese el nombre de la nueva tipoGanado'}
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <div className="space-y-2">
              <Label htmlFor="tipoGanadoNombre">Nombre *</Label>
              <Input
                id="tipoGanadoNombre"
                value={tipoGanadoFormNombre}
                onChange={(e) => setTipoGanadoFormNombre(e.target.value)}
                placeholder="Ej. Toyota"
                autoFocus
                onKeyDown={(e) => e.key === 'Enter' && handleSaveTipoGanado()}
                data-testid="tipoGanado-nombre-input"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowTipoGanadoDialog(false)} disabled={savingTipoGanado}>
              Cancelar
            </Button>
            <Button onClick={handleSaveTipoGanado} disabled={savingTipoGanado} data-testid="save-tipoGanado-button">
              {savingTipoGanado ? 'Guardando...' : 'Guardar'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete TipoGanado Confirm */}
      <AlertDialog open={!!deleteTipoGanadoId} onOpenChange={() => setDeleteTipoGanadoId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>¿Eliminar tipoGanado?</AlertDialogTitle>
            <AlertDialogDescription>
              Esta acción no se puede deshacer. Si el tipo de ganado tiene categorías o razas asociadas, deberá eliminarlas primero.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={() => handleDeleteTipoGanado(deleteTipoGanadoId)}>
              Eliminar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default TipoGanados;
